// 현장 목록 「차트」 보기 계산 — 현장마다 착공~준공 막대·공정 구간·오늘 형편을 기간 창의 칸으로 바꾼다(화면은 칸만 그린다).

import type { ProjectPhaseScheduleRef } from "../../data/repo";
import { EMPTY_MARK } from "../../lib/format";
import { matchesTokens, searchTokens } from "../list/search";
import {
  addDays,
  colOf,
  daysBetween,
  hasPlan,
  isBoardMonth,
  overdueSpanOf,
  shiftMonth,
  spanOf,
  type ChartRange,
  type ChartSpan,
  type ChartTick,
} from "./phase-chart";
import {
  countsPhaseDelay,
  DELAY_KINDS,
  phaseSchedule,
  phaseScheduleText,
  type PhaseSchedule,
} from "./phase-schedule";
import type { Project, ProjectStatus } from "./types";
import { dDayLabel, dDayOf, projectNameOf, todayKst } from "./view";

// 차트에 오르는 현장 — 유지보수·완료는 공정이 끝나 빠진다.
export const SITE_CHART_STATUSES: readonly ProjectStatus[] = ["SCHEDULED", "IN_PROGRESS"];
export const SITE_CHART_MONTHS = 3;
// 한 주에 이만큼 이상 겹치면 「동시 진행」 막대를 경고색으로.
export const SITE_LOAD_BUSY = 5;

export type SiteChartProject = Pick<
  Project,
  | "id"
  | "title"
  | "site_address"
  | "status"
  | "assignee_id"
  | "customer_id"
  | "planned_start_date"
  | "planned_end_date"
  | "created_at"
>;

export type SiteChartPhase = ProjectPhaseScheduleRef;

export type SitePhaseState = "done" | "now" | "hold" | "plan" | "late";

export interface SitePhaseRow {
  id: string;
  name: string;
  state: SitePhaseState;
  startDate: string | null;
  endDate: string | null;
  span: ChartSpan | null;
  // 계획 끝날을 넘겨 아직 안 끝난 구간(끝날 다음 날 ~ 오늘).
  overdue: ChartSpan | null;
  schedule: PhaseSchedule;
  stateText: string;
}

export interface SiteRow {
  id: string;
  name: string;
  assignee: string;
  customer: string;
  status: ProjectStatus;
  startDate: string | null;
  endDate: string | null;
  // 착공~준공 전체(예정 색).
  plan: ChartSpan | null;
  // 착공~오늘(끝난 색) — 시공중만.
  elapsed: ChartSpan | null;
  // 오늘 한 칸(진행·지연 색) — 시공중이고 오늘이 창 안일 때만.
  todayMark: ChartSpan | null;
  // 막대 글자 — 착공 전은 「착공 → 준공」, 시공중은 지금 공정과 n일째.
  label: string;
  delayed: boolean;
  endCol: number | null;
  // 준공일이 창 뒤 — 오른쪽 끝에 「n월 →」.
  endAfterLabel: string | null;
  phaseDone: number;
  phaseTotal: number;
  unscheduled: number;
  endText: string;
  phases: SitePhaseRow[];
}

export interface SiteLoad {
  from: number;
  span: number;
  count: number;
  busy: boolean;
}

export interface SiteChart {
  range: ChartRange;
  month: string;
  period: string;
  months: ChartTick[];
  // 월요일 칸 — 눈금·세로 줄.
  ticks: ChartTick[];
  todayCol: number | null;
  holidayCols: number[];
  rows: SiteRow[];
  load: SiteLoad[];
  delayedSites: number;
  startsThisMonth: number;
  endsThisMonth: number;
}

// URL 의 `?from=YYYY-MM` — 달력과 같은 범위(1900~2199)일 때만, 아니면 오늘이 든 달.
export const chartMonthOf = (value: string | string[] | undefined, today: string): string => {
  const raw = Array.isArray(value) ? value[0] : value;
  return isBoardMonth(raw) ? raw : today.slice(0, 7);
};

const minDate = (dates: readonly string[]): string | null => (dates.length ? dates.reduce((a, b) => (b < a ? b : a)) : null);
const maxDate = (dates: readonly string[]): string | null => (dates.length ? dates.reduce((a, b) => (b > a ? b : a)) : null);

// 첫 달 1일부터 months 달 끝날까지.
export const siteChartRange = (month: string, months: number = SITE_CHART_MONTHS): ChartRange => {
  const start = `${month}-01`;
  const end = addDays(`${shiftMonth(month, months)}-01`, -1);
  return { start, end, days: daysBetween(start, end) + 1 };
};

const monthLabel = (month: string): string => `${Number(month.slice(5, 7))}월`;

// 「2026년 9월 – 11월」 · 해를 넘기면 「2026년 11월 – 2027년 1월」.
export const siteChartPeriodText = (month: string, months: number = SITE_CHART_MONTHS): string => {
  const last = shiftMonth(month, months - 1);
  const head = `${month.slice(0, 4)}년 ${monthLabel(month)}`;
  return last.slice(0, 4) === month.slice(0, 4) ? `${head} – ${monthLabel(last)}` : `${head} – ${last.slice(0, 4)}년 ${monthLabel(last)}`;
};

// 차트가 읽을 상태 — 상태 칩이 차트 밖 상태(유지보수·완료)면 빈 목록.
export const chartStatusesOf = (status: ProjectStatus | null): ProjectStatus[] =>
  status === null ? [...SITE_CHART_STATUSES] : SITE_CHART_STATUSES.filter((s) => s === status);

// 차트·달력이 읽을 현장 — 기간 창과 겹칠 수 있는 것만(고객·공정 조회 전에 거른다). 착공 전·시공중과 날짜를 모르는 현장은 남긴다.
export const siteMayOverlap = (
  project: Pick<Project, "status" | "planned_start_date" | "planned_end_date" | "completion_date" | "started_at">,
  range: Pick<ChartRange, "start" | "end">
): boolean => {
  if (project.status === "SCHEDULED" || project.status === "IN_PROGRESS") return true;
  const startedOn = project.started_at === null ? null : todayKst(new Date(project.started_at));
  const start = minDate([project.planned_start_date, startedOn].filter((d): d is string => d !== null));
  if (start !== null && start > range.end) return false;
  const end = maxDate([project.completion_date, project.planned_end_date].filter((d): d is string => d !== null));
  return end === null || end >= range.start;
};

// 검색어 — 목록과 같은 토막 규칙(현장명·주소·고객 이름 중 하나에 토막마다 든다).
export const matchesChartQuery = (
  project: Pick<Project, "title" | "site_address">,
  customerName: string | undefined,
  q: string
): boolean => matchesTokens([project.title, project.site_address, customerName], searchTokens(q));

// 공정 한 칸의 형편 — 지연은 phaseSchedule·DELAY_KINDS 한 벌이고, 지연을 세는 현장(countsPhaseDelay)에서만. 보류는 진행이 아니다.
export const sitePhaseStateOf = (phase: SiteChartPhase, today: string, projectStatus: ProjectStatus): SitePhaseState => {
  if (phase.status === "DONE") return "done";
  if (countsPhaseDelay(projectStatus) && DELAY_KINDS.includes(phaseSchedule(phase, today).kind)) return "late";
  if (phase.status === "HOLD") return "hold";
  if (phase.status === "ACTIVE") return "now";
  return "plan";
};

const bySortOrder = <P extends SiteChartPhase>(phases: readonly P[]): P[] => [...phases].sort((a, b) => a.sort_order - b.sort_order);

// 지금 하는 공정 — 진행만(보류는 착수 기록이 있어도 뺀다), 공정 순서대로.
export const currentPhasesOf = <P extends SiteChartPhase>(phases: readonly P[]): P[] =>
  bySortOrder(phases.filter((p) => p.status === "ACTIVE"));

// 보류한 공정 — 공정 순서대로.
export const heldPhasesOf = <P extends SiteChartPhase>(phases: readonly P[]): P[] =>
  bySortOrder(phases.filter((p) => p.status === "HOLD"));

// 그 공정 며칠째 — 착수일(없으면 계획 시작일)부터 오늘까지, 아직 오지 않았으면 null.
export const phaseDayOf = (phase: SiteChartPhase, today: string): number | null => {
  const base = phase.actual_start_date ?? phase.start_date;
  return base !== null && base <= today ? daysBetween(base, today) + 1 : null;
};

const nameOfPhase = (phase: SiteChartPhase, names: ReadonlyMap<string, string>): string =>
  names.get(phase.phase_code) ?? phase.phase_code;

// 목록 「오늘 공정」 칸 — 「도장 · 3일째」 · 둘 이상이면 「외 1」 · 보류는 「· 보류 1」 · 보류만 있으면 「도장 보류」 · 없으면 null.
export const currentPhaseTextOf = (
  phases: readonly SiteChartPhase[],
  today: string,
  names: ReadonlyMap<string, string>
): string | null => {
  const [first, ...rest] = currentPhasesOf(phases);
  const held = heldPhasesOf(phases);
  if (!first) {
    const [h, ...more] = held;
    if (!h) return null;
    const head = `${nameOfPhase(h, names)} 보류`;
    return more.length > 0 ? `${head} 외 ${more.length}` : head;
  }
  const day = phaseDayOf(first, today);
  const head = day === null ? nameOfPhase(first, names) : `${nameOfPhase(first, names)} · ${day}일째`;
  const withRest = rest.length > 0 ? `${head} 외 ${rest.length}` : head;
  return held.length > 0 ? `${withRest} · 보류 ${held.length}` : withRest;
};

export interface ListPhaseProgress {
  done: number;
  total: number;
  // 「MM-DD 까지」 — 지금 공정(여럿이면 공정 순서 첫째)의 끝날, 지금 공정이 없으면 공정 전체의 마지막 끝날.
  end: string | null;
}

// 목록 「오늘 공정」 둘째 줄 — 「공정 5/11 · 10-03 까지」.
export const listPhaseProgressOf = (phases: readonly SiteChartPhase[]): ListPhaseProgress | null => {
  if (phases.length === 0) return null;
  const [current] = currentPhasesOf(phases);
  const ends = phases.flatMap((p) => (p.end_date ? [p.end_date] : []));
  return {
    done: phases.filter((p) => p.status === "DONE").length,
    total: phases.length,
    end: current ? current.end_date : ends.length ? ends.reduce((a, b) => (b > a ? b : a)) : null,
  };
};

const compareText = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

// 착공 빠른 순 — 날짜 없는 현장은 뒤, 같으면 이름·id 순.
const compareSiteRows = (a: SiteRow, b: SiteRow): number =>
  compareText(a.startDate ?? "9999", b.startDate ?? "9999") || a.name.localeCompare(b.name, "ko") || compareText(a.id, b.id);

const STATE_TEXT: Record<Exclude<SitePhaseState, "late">, string> = { done: "끝", now: "진행 중", hold: "보류", plan: "" };

const phaseRowOf = (
  phase: SiteChartPhase,
  range: ChartRange,
  today: string,
  projectStatus: ProjectStatus,
  names: ReadonlyMap<string, string>
): SitePhaseRow => {
  const state = sitePhaseStateOf(phase, today, projectStatus);
  const schedule = phaseSchedule(phase, today);
  const planned = hasPlan(phase);
  // 꼬리 칸 수 = 상태 글자 일수(overdueSpanOf) — 일수 글자는 꼬리에 없고 상태 글자가 말한다.
  const overdue = state === "late" && planned ? overdueSpanOf(phase, schedule, today, range) : null;
  return {
    id: phase.id,
    name: nameOfPhase(phase, names),
    state,
    startDate: phase.start_date,
    endDate: phase.end_date,
    span: planned ? spanOf(range, phase.start_date, phase.end_date) : null,
    overdue,
    schedule,
    stateText:
      state !== "late" ? STATE_TEXT[state] : phase.status === "HOLD" ? `보류 · ${phaseScheduleText(schedule)}` : phaseScheduleText(schedule),
  };
};

// 막대 글자에 올릴 지금 공정 수 — 넘치면 「외 n」.
const LABEL_PHASES = 2;

const siteRowOf = (
  project: SiteChartProject,
  phases: readonly SiteChartPhase[],
  input: Pick<SiteChartInput, "names" | "assignees" | "customers" | "today">,
  range: ChartRange
): SiteRow => {
  const { today, names } = input;
  const ordered = [...phases].sort((a, b) => a.sort_order - b.sort_order || compareText(a.id, b.id));
  const planned = ordered.filter(hasPlan);
  const startDate = project.planned_start_date ?? minDate(planned.map((p) => p.start_date));
  const endDate = project.planned_end_date ?? maxDate(planned.map((p) => p.end_date));
  const phaseRows = ordered.map((p) => phaseRowOf(p, range, today, project.status, names));
  const delayed = phaseRows.some((p) => p.state === "late");
  const started = project.status === "IN_PROGRESS";

  let label = "";
  if (!started) {
    label = `착공 ${startDate?.slice(5) ?? "미정"} → 준공 ${endDate?.slice(5) ?? "미정"}`;
  } else {
    const current = currentPhasesOf(ordered);
    const parts = current.slice(0, LABEL_PHASES).map((p) => {
      const day = phaseDayOf(p, today);
      return day === null ? nameOfPhase(p, names) : `${nameOfPhase(p, names)} ${day}일째`;
    });
    if (current.length > LABEL_PHASES) parts.push(`외 ${current.length - LABEL_PHASES}`);
    const held = heldPhasesOf(ordered).length;
    if (held > 0) parts.push(`보류 ${held}`);
    if (delayed) parts.push("지연");
    label = parts.join(" · ");
  }

  const elapsedEnd = endDate !== null && endDate < today ? endDate : today;
  const endCol = endDate === null ? null : colOf(range, endDate);
  const endAfter = endDate !== null && endDate > range.end;
  const endDDay = dDayLabel(dDayOf(endDate, today));

  return {
    id: project.id,
    name: projectNameOf(project),
    assignee: (project.assignee_id && input.assignees.get(project.assignee_id)) || "미지정",
    customer: input.customers.get(project.customer_id) ?? EMPTY_MARK,
    status: project.status,
    startDate,
    endDate,
    plan: startDate !== null && endDate !== null ? spanOf(range, startDate, endDate) : null,
    elapsed: started && startDate !== null && startDate <= today ? spanOf(range, startDate, elapsedEnd) : null,
    todayMark: started ? spanOf(range, today, today) : null,
    label,
    delayed,
    endCol,
    endAfterLabel: endAfter && endDate !== null ? `${Number(endDate.slice(5, 7))}월 →` : null,
    phaseDone: ordered.filter((p) => p.status === "DONE").length,
    phaseTotal: ordered.length,
    unscheduled: ordered.length - planned.length,
    endText: endDate === null ? "준공 미정" : `준공 ${endDate.slice(5)} · ${endDDay}`,
    phases: phaseRows,
  };
};

// 날짜 영역이 가장 좁을 때(lg) 폭 · 석 달 창의 가장 긴 날 수 — 화면 GRID_CLASS 의 lg 최소폭과 같은 값.
export const SITE_CHART_MIN_TRACK_PX = 420;
export const SITE_CHART_MAX_DAYS = 92;
// 막대 글자가 칸에 다 드는지 — 가장 좁은 하루 폭·11px 글자(한글 11px, 그 밖 6.5px)·좌우 여백 12px 로 어림한다.
export const BAR_MIN_DAY_PX = SITE_CHART_MIN_TRACK_PX / SITE_CHART_MAX_DAYS;
export const barLabelFits = (text: string, days: number): boolean => {
  const width = [...text].reduce((sum, ch) => sum + (/[\u3131-\ud79d]/.test(ch) ? 11 : 6.5), 12);
  return days * BAR_MIN_DAY_PX >= width;
};

// 오늘 뒤 남은 칸이 이보다 적고 앞쪽이 더 넓으면 오늘 글자(진행 글자·「오늘」 표시)를 오늘 선 왼쪽에 붙인다.
export const LABEL_MIN_COLS = 21;
export const todaySideOf = (todayCol: number, days: number): "before" | "after" => {
  const roomAfter = days - todayCol - 1;
  return roomAfter < LABEL_MIN_COLS && todayCol > roomAfter ? "before" : "after";
};

// 머리 칸 세 줄(px) — 달 · 「오늘」 · 주 눈금. 줄 높이를 못박는다(text-xs 는 줄 높이를 물려받아 16.8px 로 커져 「오늘」 줄을 0.8px 덮었다).
// 머리 칸 안쪽 높이 — 화면 h-14(56px)에서 아래 테두리 1px 를 뺀 값.
export const SITE_CHART_HEAD_PX = 55;
export interface HeadRow {
  top: number;
  height: number;
}
export const SITE_CHART_HEAD_ROWS = {
  month: { top: 2, height: 16 },
  today: { top: 18, height: 16 },
  tick: { top: 36, height: 16 },
} as const satisfies Record<string, HeadRow>;

// 머리 줄끼리 겹치지 않고 칸 안에 드는지 — 위에서부터 다음 줄 top 이 앞 줄 끝 이상.
export const headRowsClear = (rows: readonly HeadRow[], total: number = SITE_CHART_HEAD_PX): boolean => {
  const sorted = [...rows].sort((a, b) => a.top - b.top);
  return sorted.every(
    (r, i) => r.top >= 0 && r.top + r.height <= total && (i === 0 || r.top >= sorted[i - 1]!.top + sorted[i - 1]!.height)
  );
};

// 창 안의 월요일 칸.
const mondayCols = (range: ChartRange): number[] => {
  const dow = new Date(`${range.start}T00:00:00Z`).getUTCDay();
  const cols: number[] = [];
  for (let col = (8 - dow) % 7; col < range.days; col += 7) cols.push(col);
  return cols;
};

// 주마다 겹치는 현장 수 — 첫 월요일 앞 토막도 한 주로 친다.
const loadOf = (range: ChartRange, mondays: readonly number[], rows: readonly SiteRow[]): SiteLoad[] => {
  const starts = mondays[0] === 0 ? [...mondays] : [0, ...mondays];
  return starts.map((from, i) => {
    const to = Math.min((starts[i + 1] ?? range.days) - 1, range.days - 1);
    const weekStart = addDays(range.start, from);
    const weekEnd = addDays(range.start, to);
    const count = rows.filter(
      (r) => r.startDate !== null && r.endDate !== null && r.startDate <= weekEnd && r.endDate >= weekStart
    ).length;
    return { from, span: to - from + 1, count, busy: count >= SITE_LOAD_BUSY };
  });
};

export interface SiteChartInput {
  projects: readonly SiteChartProject[];
  phases: readonly SiteChartPhase[];
  // 공정 코드 → 그 회사에게 보이는 이름.
  names: ReadonlyMap<string, string>;
  assignees: ReadonlyMap<string, string>;
  customers: ReadonlyMap<string, string>;
  today: string;
  // 창 첫 달 `YYYY-MM`.
  month: string;
  holidays: Iterable<string>;
}

export const buildSiteChart = (input: SiteChartInput): SiteChart => {
  const range = siteChartRange(input.month);
  const phasesOf = new Map<string, SiteChartPhase[]>();
  for (const phase of input.phases) phasesOf.set(phase.project_id, [...(phasesOf.get(phase.project_id) ?? []), phase]);

  const rows = input.projects
    .filter((p) => SITE_CHART_STATUSES.includes(p.status))
    .map((p) => siteRowOf(p, phasesOf.get(p.id) ?? [], input, range))
    .sort(compareSiteRows);

  const mondays = mondayCols(range);
  const thisMonth = input.today.slice(0, 7);
  const months: ChartTick[] = [];
  for (let m = input.month; m <= range.end.slice(0, 7); m = shiftMonth(m, 1)) {
    months.push({ col: daysBetween(range.start, `${m}-01`), label: monthLabel(m) });
  }

  return {
    range,
    month: input.month,
    period: siteChartPeriodText(input.month),
    months,
    ticks: mondays.map((col) => ({ col, label: addDays(range.start, col).slice(8) })),
    todayCol: colOf(range, input.today),
    holidayCols: [...new Set(input.holidays)]
      .map((d) => colOf(range, d))
      .filter((c): c is number => c !== null)
      .sort((a, b) => a - b),
    rows,
    load: loadOf(range, mondays, rows),
    delayedSites: rows.filter((r) => r.delayed).length,
    startsThisMonth: rows.filter((r) => r.startDate?.slice(0, 7) === thisMonth).length,
    endsThisMonth: rows.filter((r) => r.endDate?.slice(0, 7) === thisMonth).length,
  };
};
